import api from '../lib/axios';
import type { ApiResponse, FlowStep, FieldSpec, DetailSpecField, ImportResult, ImportTC } from '@maios/shared';

// --- Types ---
export interface SpecRequirement {
  id: string;
  reqId: string;
  title: string;
}

export interface SpecAssignee {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface SpecListItem {
  id: string;
  projectId: string;
  requirementId: string;
  functionCode: string;
  title: string;
  actor: string | null;
  assigneeId: string | null;
  assignee: SpecAssignee | null;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  requirement: SpecRequirement;
  _count: { testCases: number; linkedIssues: number };
}

export interface SpecHistoryItem {
  id: string;
  specId: string;
  version: number;
  snapshot: Record<string, unknown>;
  changedBy: string;
  changeNote: string | null;
  changedAt: string;
  changer: { id: string; name: string; email: string; avatarUrl: string | null };
}

export interface SpecDiffItem {
  field: string;
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  old: unknown;
  new: unknown;
}

export interface SpecDiffResult {
  v1: number;
  v2: number;
  diffs: SpecDiffItem[];
}

export interface SpecDetail extends SpecListItem {
  precondition: string | null;
  postcondition: string | null;
  mainFlow: FlowStep[] | null;
  alternativeFlow: FlowStep[] | null;
  exceptionFlow: FlowStep[] | null;
  inputSpec: (FieldSpec | DetailSpecField)[] | null;
  outputSpec: FieldSpec[] | null;
  businessRules: string[];
  uiReference: string | null;
  testCases: {
    id: string;
    title: string;
    type: string;
    status: string;
    priority: string;
  }[];
}

export interface CreateSpecData {
  requirementId: string;
  functionCode: string;
  title: string;
  actor?: string;
  precondition?: string;
  postcondition?: string;
  mainFlow?: FlowStep[];
  alternativeFlow?: FlowStep[];
  exceptionFlow?: FlowStep[];
  inputSpec?: DetailSpecField[];
  outputSpec?: FieldSpec[];
  businessRules?: string[];
  uiReference?: string;
  assigneeId?: string;
  status?: string;
}

export type UpdateSpecData = Partial<CreateSpecData>;

export interface GeneratedTestCase {
  title: string;
  type: string;
  precondition: string;
  steps: { step: number; action: string; expected: string }[];
  expectedResult: string;
  priority: string;
}

// --- API calls ---
export async function fetchSpecs(projectId: string, params?: { requirementId?: string; status?: string }) {
  const query = new URLSearchParams();
  if (params?.requirementId) query.set('requirementId', params.requirementId);
  if (params?.status) query.set('status', params.status);
  const qs = query.toString();
  const res = await api.get<ApiResponse<SpecListItem[]>>(
    `/projects/${projectId}/specs${qs ? `?${qs}` : ''}`,
  );
  return res.data.data!;
}

export async function fetchSpec(projectId: string, specId: string) {
  const res = await api.get<ApiResponse<SpecDetail>>(`/projects/${projectId}/specs/${specId}`);
  return res.data.data!;
}

export async function createSpec(projectId: string, data: CreateSpecData) {
  const res = await api.post<ApiResponse<SpecDetail>>(`/projects/${projectId}/specs`, data);
  return res.data.data!;
}

export async function updateSpec(projectId: string, specId: string, data: UpdateSpecData) {
  const res = await api.put<ApiResponse<SpecDetail>>(`/projects/${projectId}/specs/${specId}`, data);
  return res.data.data!;
}

export async function deleteSpec(projectId: string, specId: string) {
  await api.delete(`/projects/${projectId}/specs/${specId}`);
}

export async function generateTestCases(projectId: string, specId: string) {
  const res = await api.post<ApiResponse<GeneratedTestCase[]>>(
    `/projects/${projectId}/specs/${specId}/generate-testcases`,
  );
  return res.data.data!;
}

export async function saveTestCases(
  projectId: string,
  specId: string,
  testCases: GeneratedTestCase[],
) {
  const res = await api.post<ApiResponse<unknown[]>>(
    `/projects/${projectId}/specs/${specId}/save-testcases`,
    { testCases },
  );
  return res.data.data!;
}

export async function fetchSpecHistory(projectId: string, specId: string) {
  const res = await api.get<ApiResponse<SpecHistoryItem[]>>(
    `/projects/${projectId}/specs/${specId}/history`,
  );
  return res.data.data!;
}

export async function fetchSpecDiff(projectId: string, specId: string, v1: number, v2: number) {
  const res = await api.get<ApiResponse<SpecDiffResult>>(
    `/projects/${projectId}/specs/${specId}/diff?v1=${v1}&v2=${v2}`,
  );
  return res.data.data!;
}

// --- 마크다운 추출 ---

/** Content-Disposition 헤더에서 filename 추출 */
function extractFilename(contentDisposition: string | null | undefined, fallback: string): string {
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match) return match[1];
  }
  return fallback;
}

/** Blob을 브라우저 다운로드로 트리거 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** 개별 명세 마크다운 추출 */
export async function exportMarkdown(projectId: string, specId: string) {
  const res = await api.get(`/projects/${projectId}/specs/${specId}/export-markdown`, {
    responseType: 'blob',
  });
  const filename = extractFilename(
    res.headers['content-disposition'],
    `SPEC_${specId}.md`,
  );
  downloadBlob(res.data as Blob, filename);
}

/** 일괄 명세 마크다운 추출 */
export async function exportAllMarkdown(projectId: string, status?: string) {
  const query = status && status !== 'ALL' ? `?status=${status}` : '';
  const res = await api.get(`/projects/${projectId}/specs/export-markdown${query}`, {
    responseType: 'blob',
  });
  const filename = extractFilename(
    res.headers['content-disposition'],
    `SPECS_${projectId}.md`,
  );
  downloadBlob(res.data as Blob, filename);
}

// --- TC Import ---

/** TC Import 미리보기 — JSON 문자열 */
export async function importPreviewJSON(projectId: string, specId: string, jsonContent: string) {
  const res = await api.post<ApiResponse<ImportResult>>(
    `/projects/${projectId}/specs/${specId}/import-testcases/preview`,
    { jsonContent },
  );
  return res.data.data!;
}

/** TC Import 미리보기 — XLSX 파일 */
export async function importPreviewXLSX(projectId: string, specId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<ApiResponse<ImportResult>>(
    `/projects/${projectId}/specs/${specId}/import-testcases/preview-xlsx`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data!;
}

/** TC Import 확정 저장 */
export async function importConfirm(
  projectId: string,
  specId: string,
  testCases: ImportTC[],
) {
  const res = await api.post<{ success: boolean; imported: number; testCaseIds: string[] }>(
    `/projects/${projectId}/specs/${specId}/import-testcases/confirm`,
    { testCases },
  );
  return res.data;
}

// --- Spec Import ---

export interface SpecImportPreviewItem {
  index: number;
  functionCode: string;
  title: string;
  mainFlowStepCount: number;
  status: string;
  warnings: string[];
  mapped: Record<string, unknown>;
}

export interface SpecImportErrorItem {
  index: number;
  errors: string[];
  raw: unknown;
}

export interface SpecImportConflictItem {
  index: number;
  functionCode: string;
  existingSpecId: string;
  existingTitle: string;
}

export interface SpecImportPreviewResult {
  valid: SpecImportPreviewItem[];
  warnings: SpecImportPreviewItem[];
  errors: SpecImportErrorItem[];
  conflicts: SpecImportConflictItem[];
  sourceType: 'json' | 'xlsx';
  xlsxInfo?: { sheetName: string; totalRows: number; filteredRows: number };
}

export interface SpecImportConfirmItem {
  index: number;
  mapped: Record<string, unknown>;
  conflictResolution?: 'overwrite' | 'skip' | 'create_new';
}

/** Spec Import 미리보기 — JSON */
export async function specImportPreviewJSON(projectId: string, data: unknown, requirementId?: string) {
  const res = await api.post<ApiResponse<SpecImportPreviewResult>>(
    `/projects/${projectId}/specs/import/preview`,
    { data, requirementId },
  );
  return res.data.data!;
}

/** Spec Import 미리보기 — XLSX */
export async function specImportPreviewXLSX(projectId: string, file: File, requirementId?: string) {
  const formData = new FormData();
  formData.append('file', file);
  if (requirementId) formData.append('requirementId', requirementId);
  const res = await api.post<ApiResponse<SpecImportPreviewResult>>(
    `/projects/${projectId}/specs/import/preview-xlsx`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data!;
}

/** Spec Import 확정 저장 */
export async function specImportConfirm(
  projectId: string,
  items: SpecImportConfirmItem[],
  requirementId: string,
) {
  const res = await api.post<{ success: boolean; importedCount: number; message: string }>(
    `/projects/${projectId}/specs/import/confirm`,
    { items, requirementId },
  );
  return res.data;
}
